import PropTypes from 'prop-types';
import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const definingYourBrandIdentityComprehensiveGuideContents = [
  `Understand Your Brand's Purpose and Values`,
  'Identify Your Target Audience',
  'Define Your Brand Personality',
  'Develop Your Brand Voice and Messaging',
  'Create a Visual Identity',
  'Establish Brand Guidelines',
  'Communicate Your Brand Consistently',
  'Evolve Your Brand Over Time',
  `Measure Your Brand's Impact`,
  'Seek Feedback and Iterate',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const DefiningYourBrandIdentityComprehensiveGuideArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Your brand identity is the essence of your business – it&apos;s how you
        differentiate yourself from competitors and create a connection with
        your audience. Defining your brand identity is a crucial step in
        establishing a strong and memorable brand that resonates with your
        target market. This guide will walk you through the key components of
        brand identity and provide actionable tips on how to define and
        communicate your brand effectively.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Understand Your Brand&apos;s Purpose and Values
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Defining Your Why
        </Layout.Card.Text>
        <Layout.Card.Text>
          Start by asking yourself why your business exists and what values it
          stands for. Your brand&apos;s purpose should go beyond making a profit
          and should resonate with your target audience on a deeper level. For
          example, if you&apos;re a sustainable fashion brand, your purpose
          might be to reduce the environmental impact of the fashion industry.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Identify Your Target Audience
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Understanding Your Audience
        </Layout.Card.Text>
        <Layout.Card.Text>
          To effectively communicate your brand identity, you need to understand
          who your target audience is. Conduct market research to identify their
          demographics, interests, and pain points. This information will help
          you tailor your brand messaging to resonate with your audience.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Define Your Brand Personality
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Giving Your Brand a Personality
        </Layout.Card.Text>
        <Layout.Card.Text>
          Your brand personality is the human traits and characteristics that
          you assign to your brand. Are you playful and fun, or serious and
          professional? Define a set of personality traits that align with your
          brand values and resonate with your target audience.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Develop Your Brand Voice and Messaging
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Communicating Your Brand
        </Layout.Card.Text>
        <Layout.Card.Text>
          Your brand voice is the tone and style of your communication. It
          should be consistent across all channels and reflect your brand&apos;s
          personality. Develop key messaging that communicates your brand&apos;s
          values, benefits, and unique selling points.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${4}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Create a Visual Identity
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Visualizing Your Brand
        </Layout.Card.Text>
        <Layout.Card.Text>
          Your visual identity includes your logo, color palette, typography,
          and imagery. These elements should be cohesive and reflect your
          brand&apos;s personality and values. Your logo, in particular, should
          be memorable and easily recognizable.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${5}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Establish Brand Guidelines
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Setting Consistent Standards
        </Layout.Card.Text>
        <Layout.Card.Text>
          To maintain consistency across all brand touchpoints, establish brand
          guidelines that outline how your brand should be represented. This
          includes rules for logo usage, color schemes, typography, and tone of
          voice.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${6}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Communicate Your Brand Consistently
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Building Brand Recognition
        </Layout.Card.Text>
        <Layout.Card.Text>
          Consistency is key to building a strong brand identity. Ensure that
          your brand is represented consistently across all channels, including
          your website, social media, and marketing materials. This will help
          build brand recognition and trust with your audience.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${7}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Evolve Your Brand Over Time
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Adapting to Change
        </Layout.Card.Text>
        <Layout.Card.Text>
          As your business grows and evolves, your brand identity may need to
          adapt. Stay attuned to market trends and customer feedback, and be
          willing to evolve your brand to stay relevant and competitive.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${8}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Measure Your Brand&apos;s Impact
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Tracking Your Brand&apos;s Performance
        </Layout.Card.Text>
        <Layout.Card.Text>
          Use metrics such as brand awareness, brand perception, and customer
          loyalty to measure the impact of your brand identity. This will help
          you understand what&apos;s working and identify areas for improvement.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${9}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Seek Feedback and Iterate
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Continuously Improving Your Brand
        </Layout.Card.Text>
        <Layout.Card.Text>
          Finally, seek feedback from customers, employees, and other
          stakeholders to understand how your brand is perceived. Use this
          feedback to iterate and refine your brand identity over time.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Defining your brand identity is a foundational step in building a
        successful and enduring brand. By understanding your brand&apos;s
        purpose and values, identifying your target audience, and creating a
        cohesive visual and verbal identity, you can create a brand that
        resonates with your audience and sets you apart from the competition.
      </Layout.Card.Text>
      <Layout.Card.Text>
        For expert guidance on defining your brand identity, visit&nbsp;
        <Link href="/">Incorpified</Link>. Our team of branding experts can help
        you create a brand identity that reflects your values, resonates with
        your audience, and drives business success.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

DefiningYourBrandIdentityComprehensiveGuideArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default DefiningYourBrandIdentityComprehensiveGuideArticle;
